/**
 * Named problem-type chip: white bordered chip with an inner severity bar +
 * human label, replacing the legacy emoji flag string for runs that carry a
 * problem_type slug. Label/meaning/action come from the backend registry
 * (useProblemTypes); unknown or still-loading slugs degrade to the raw slug.
 * Visual spec: the approved audit-remodel mockup artifact `.chip` (white
 * surface, 1px border, 3×14 severity bar, 12.5px/500); colors via THEME only.
 */
import React from "react"

import { THEME } from "../../config/theme"
import { useProblemTypes } from "../../hooks/useProblemTypes"

interface Props {
  slug: string
  /** Duplicate-VIN subtype etc., shown as quiet detail after the label. */
  detail?: string | null
}

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>

export default function ProblemChip({ slug, detail }: Props) {
  const registry = useProblemTypes()
  const info = registry?.[slug]

  const label = info?.label ?? slug
  const severity = info?.severity ?? "cleanup"
  const accent = SEVERITY_COLORS[severity] ?? THEME.colors.semantic.info
  const tooltip = info ? `${info.label} — ${info.meaning} Action: ${info.action}` : slug

  const chipStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: THEME.colors.white,
    border: `1px solid ${THEME.colors.neutral.light.border}`,
    borderRadius: THEME.borderRadius.sm,
    padding: "4px 10px 4px 8px",
    fontSize: 12.5,
    fontWeight: 500,
    color: THEME.colors.neutral.light.textPrimary,
    whiteSpace: "nowrap",
  }

  const barStyle: React.CSSProperties = {
    width: THEME.problemDisplay.chipAccentBarWidth,
    height: 14,
    borderRadius: 2,
    flex: "none",
    background: accent,
  }

  return (
    <span style={chipStyle} title={tooltip} aria-label={tooltip} role="img">
      <span style={barStyle} />
      {label}
      {detail ? (
        <span style={{ color: THEME.colors.neutral.light.textSecondary, fontWeight: 400 }}>
          {detail}
        </span>
      ) : null}
    </span>
  )
}
